import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuditTable = ({ 
  auditEntries, 
  selectedEntries, 
  onSelectionChange, 
  onViewDetails,
  onSort,
  sortConfig 
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(auditEntries?.map(entry => entry?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectEntry = (entryId, checked) => {
    if (checked) {
      onSelectionChange([...selectedEntries, entryId]);
    } else {
      onSelectionChange(selectedEntries?.filter(id => id !== entryId));
    }
  };

  const toggleRowExpansion = (entryId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(entryId)) {
      newExpanded?.delete(entryId);
    } else {
      newExpanded?.add(entryId);
    }
    setExpandedRows(newExpanded);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return 'Plus';
      case 'modified': return 'Edit';
      case 'reviewed': return 'CheckCircle';
      case 'exported': return 'Download';
      case 'deleted': return 'Trash2';
      default: return 'Activity';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return 'text-success';
      case 'modified': return 'text-warning';
      case 'reviewed': return 'text-primary';
      case 'exported': return 'text-accent';
      case 'deleted': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const renderChangeDetails = (changes) => {
    if (!changes || changes?.length === 0) return null;

    const headerChanges = [];
    const lineGroups = {};

    const friendly = (field) => {
      const map = {
        vendorName: 'Vendor Name',
        vendorAddress: 'Vendor Address',
        vendorVatNumber: 'Vendor VAT',
        invoiceNumber: 'Invoice #',
        invoiceDate: 'Invoice Date',
        dueDate: 'Due Date',
        currency: 'Currency',
        vendorId: 'Vendor Ref',
        description: 'Description',
        quantity: 'Qty',
        unitPrice: 'Unit Price',
        vatRate: 'VAT %',
        vatCode: 'VAT Code',
        netTotal: 'Net',
        vatAmount: 'VAT',
        total: 'Total',
      };
      return map[field] || field;
    };

    const fmt = (v) => {
      if (v === undefined || v === null || v === '') return '—';
      return String(v);
    };

    changes.forEach((c) => {
      const m = /^line_items\[(\d+)\]\.([^\s]+)$/.exec(c.field || '');
      if (m) {
        const idx = parseInt(m[1], 10);
        const key = m[2];
        if (!lineGroups[idx]) lineGroups[idx] = [];
        lineGroups[idx].push({ label: friendly(key), oldValue: c.oldValue, newValue: c.newValue });
      } else {
        headerChanges.push({ label: friendly(c.field), oldValue: c.oldValue, newValue: c.newValue });
      }
    });

    const Table = ({ rows }) => (
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 w-40 text-muted-foreground">Field</th>
              <th className="text-left px-3 py-2 w-1/2 text-muted-foreground">Before</th>
              <th className="text-left px-3 py-2 w-1/2 text-muted-foreground">After</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2 text-foreground">{r.label}</td>
                <td className="px-3 py-2 text-error line-through break-all">{fmt(r.oldValue)}</td>
                <td className="px-3 py-2 text-success break-all">{fmt(r.newValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    return (
      <div className="space-y-4">
        {headerChanges.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-muted-foreground mb-2">Header</h5>
            <Table rows={headerChanges} />
          </div>
        )}
        {Object.keys(lineGroups).sort((a,b)=>a-b).map((idx) => (
          <div key={idx}>
            <h5 className="text-xs font-semibold text-muted-foreground mb-2">Line {Number(idx)+1}</h5>
            <Table rows={lineGroups[idx]} />
          </div>
        ))}
      </div>
    );
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={selectedEntries?.length === auditEntries?.length && auditEntries?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="w-12 p-4"></th>
              <th className="text-left p-4">
                <button
                  onClick={() => onSort('timestamp')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Timestamp</span>
                  <Icon name={getSortIcon('timestamp')} size={14} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => onSort('user')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>User</span>
                  <Icon name={getSortIcon('user')} size={14} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => onSort('action')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Action</span>
                  <Icon name={getSortIcon('action')} size={14} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => onSort('invoiceReference')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Invoice</span>
                  <Icon name={getSortIcon('invoiceReference')} size={14} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => onSort('vendorName')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Vendor</span>
                  <Icon name={getSortIcon('vendorName')} size={14} />
                </button>
              </th>
              <th className="text-left p-4">Fields Changed</th>
              <th className="text-left p-4">Details</th>
              <th className="w-24 p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {auditEntries?.map((entry) => (
              <React.Fragment key={entry?.id}>
                <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="p-4">
                    <Checkbox
                      checked={selectedEntries?.includes(entry?.id)}
                      onChange={(e) => handleSelectEntry(entry?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleRowExpansion(entry?.id)}
                      className="p-1 rounded hover:bg-muted transition-smooth"
                    >
                      <Icon 
                        name={expandedRows?.has(entry?.id) ? "ChevronDown" : "ChevronRight"} 
                        size={16} 
                      />
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-foreground">
                      {formatTimestamp(entry?.timestamp)}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={12} className="text-primary" />
                      </div>
                      <span className="text-sm text-foreground">{entry?.user}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={getActionIcon(entry?.action)} 
                        size={16} 
                        className={getActionColor(entry?.action)}
                      />
                      <span className={`text-sm font-medium ${getActionColor(entry?.action)}`}>
                        {entry?.action?.charAt(0)?.toUpperCase() + entry?.action?.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-foreground font-mono">
                      {entry?.invoiceReference}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-foreground">
                      {entry?.vendorName || '—'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {entry?.fieldsChanged?.slice(0, 3)?.map((field, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                        >
                          {field}
                        </span>
                      ))}
                      {entry?.fieldsChanged?.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                          +{entry?.fieldsChanged?.length - 3} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">
                      {entry?.description}
                    </span>
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(entry)}
                      iconName="Eye"
                    />
                  </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRows?.has(entry?.id) && (
                  <tr>
                    <td colSpan="9" className="p-0">
                      <div className="bg-muted/20 p-4 border-t border-border">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-3">
                              Change Details
                            </h4>
                            {renderChangeDetails(entry?.changes)}
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-3">
                              System Information
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Session ID:</span>
                                <span className="font-mono text-foreground">{entry?.sessionId}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">IP Address:</span>
                                <span className="font-mono text-foreground">{entry?.ipAddress}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">User Agent:</span>
                                <span className="text-foreground text-xs">{entry?.userAgent}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {auditEntries?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No audit entries found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or check back later for new activity.
          </p>
        </div>
      )}
    </div>
  );
};

export default AuditTable;
